import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import DateTabItem from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/DateTabItem';
import WLiveSection from '@wconcept/wconcept-ui-renewal/src/components/organisms/_section/WLiveSection';
import WLiveItem from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/WLiveItem';
import { PromotionWLiveModule } from '@wconcept/wconcept-common/models/renewal/promotion';
import { CommonResponse } from '@wconcept/wconcept-common/models/common-response';
import { ShopLiveListResponse } from '@wconcept/wconcept-common/models/renewal/shoplive';
import {
  getDateAm,
  getDateTime,
} from '@wconcept/wconcept-common/utils/liveDateConvert';
import { requestGetShopLiveAlarmStatus } from '@apis/renewal/shoplive';
import useGa4Event from '@hooks/useGa4Event';
import dayjs from '@wconcept/wconcept-common/libs/dayjs';
import useGlobalState from '@hooks/useGlobalState';
import useModalManager from '@hooks/useModalManager';
import { getCookieWckLiveAlarmValue } from '@wconcept/wconcept-common/utils/cookie';
import useLiveAlarm from '@hooks/useLiveAlarm';
import { useServerTimeActions, useServerTime } from '@store/serverTimeStore';
import useHistoryRouter from '@hooks/useLinkTo';
import { type PromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';
import {
  buildWLiveViewModel,
  resolveWLiveDisplayStatus,
  resolveWLiveEntryFocus,
  type WLiveDisplayStatus,
} from '@components/pages/promotion/utils/wLiveSchedule';

interface WLiveModuleProps {
  bgType: string;
  visualTokens: PromotionModuleVisualTokens;
  isDesktop: boolean;
  subTitle: string;
  title: string;
  wLiveData: PromotionWLiveModule[];
  // eslint-disable-next-line react/no-unused-prop-types
  promotionId?: number;
  // eslint-disable-next-line react/no-unused-prop-types
  promotionTitle?: string;
}

/** 상태 → DateTabItem status 코드 (1: 마감 | 2: 진행중 | 3: 예정) */
const statusToTabValue = (status: WLiveDisplayStatus): '1' | '2' | '3' => {
  if (status === 'ONAIR') return '2'; // 진행중
  if (status === 'READY') return '3'; // 예정
  return '1'; // 종료(마감)
};

function WLiveModule({
  bgType,
  visualTokens,
  isDesktop,
  subTitle,
  wLiveData,
  title,
}: WLiveModuleProps) {
  const { getAdjustedTime } = useServerTimeActions();
  const { serverTimeOffset } = useServerTime();

  const { appInfo, assistData } = useGlobalState();
  const { linkTo } = useHistoryRouter();
  const { sendGa4TabClick, sendGa4ButtonClick } = useGa4Event();

  const { presetBgType, customColorsFull } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'full',
  });

  const sectionColorType = customColorsFull ?? presetBgType;

  const { openLoginConfirm, openAgreeModal } = useModalManager();
  const { registerAlarm, unregisterAlarm, confirmAlarm } = useLiveAlarm();

  // 날짜 그룹 + (데이터 기반) 초기 포커스.
  const viewModel = useMemo(
    () => buildWLiveViewModel(wLiveData ?? []),
    [wLiveData]
  );

  // 선택된 날짜 탭.
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null);

  // 진입 시(마운트 후 serverTime 준비되면) 날짜 탭을 '자정 경계' 시계 기준으로 1회 선택.
  //  - 오늘(now의 날짜) 그룹 → 없으면 다음 방송일 그룹 → 그것도 없으면 마지막 그룹
  //  - 당일 라이브가 끝나도 자정까지 오늘 탭 유지
  //  - 초기 렌더는 viewModel 기준이고, 이후 보정한다.
  useEffect(() => {
    if (serverTimeOffset === null) return;
    const focus = resolveWLiveEntryFocus(
      viewModel.dateGroups,
      getAdjustedTime()
    );
    if (focus) setSelectedDateKey(prev => prev ?? focus.dateKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverTimeOffset, viewModel.dateGroups]);

  // 선택된 날짜 그룹 (없으면 포커스 그룹, 그것도 없으면 첫 그룹)
  const selectedGroup = useMemo(() => {
    const groups = viewModel.dateGroups;
    if (groups.length === 0) return null;
    return (
      groups.find(g => g.dateKey === selectedDateKey) ??
      groups.find(g => g.dateKey === viewModel.focusedDateKey) ??
      groups[0]
    );
  }, [viewModel, selectedDateKey]);

  // 스와이퍼 초기 슬라이드: 포커스 그룹을 볼 때만 포커스 라이브 인덱스, 그 외 0.
  const initialSlide =
    selectedGroup?.dateKey === viewModel.focusedDateKey
      ? viewModel.focusedSlideIndex
      : 0;

  // 라이브별 알람 상태 맵 { campaignKey: boolean }
  const [alarmMap, setAlarmMap] = useState<Record<string, boolean>>({});

  // 선택 그룹의 라이브들(최대 5개) 알람 상태 일괄 조회
  useEffect(() => {
    const lives = selectedGroup?.lives ?? [];
    if (lives.length === 0) return undefined;

    let cancelled = false;
    // 라이브 알람 상태 조회
    Promise.all(
      lives.map(async live => {
        const campaignKey = live.shopLiveInfo?.campaignKey;
        if (!campaignKey) return { campaignKey: '', on: false };
        const res = await requestGetShopLiveAlarmStatus({ campaignKey });
        return { campaignKey, on: !!res.data };
      })
    ).then(results => {
      if (cancelled) return;
      setAlarmMap(prev => {
        const next = { ...prev };
        results.forEach(({ campaignKey, on }) => {
          if (campaignKey) next[campaignKey] = on;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [selectedGroup]);

  // 라이브 단건 비디오/포스터
  const getVideo = useCallback((live: PromotionWLiveModule) => {
    const info = live.shopLiveInfo;
    if (!info) return { mainVideo: '', mainImg: '' };

    const status = resolveWLiveDisplayStatus(live);
    if (status === 'ONAIR') {
      return { mainVideo: info.previewLiveUrl, mainImg: info.bgImageUrl };
    }
    // READY · ENDED → 썸네일만
    return { mainVideo: '', mainImg: info.thumbnailImageUrl };
  }, []);

  // 알람 신청/해제 (카드별)
  const onClickAlarm = useCallback(
    async (key: string, isAlarmTalk: boolean) =>
      new Promise<CommonResponse<ShopLiveListResponse | boolean> | undefined>(
        // eslint-disable-next-line no-async-promise-executor
        async resolve => {
          const mktYn = await getCookieWckLiveAlarmValue();
          if (
            assistData.isLogin &&
            (assistData.user.liveAlarmYN === 'Y' || mktYn === 'Y')
          ) {
            // eslint-disable-next-line no-lonely-if
            if (isAlarmTalk) {
              unregisterAlarm({ campaignKey: key }).then(alaramRes => {
                if (alaramRes?.data) {
                  setAlarmMap(prev => ({ ...prev, [key]: false }));
                }
                resolve(alaramRes);
              });
            } else {
              registerAlarm({ campaignKey: key }).then(alaramRes => {
                if (alaramRes?.data) {
                  setAlarmMap(prev => ({ ...prev, [key]: true }));
                }
                resolve(alaramRes);
              });
            }
          } else if (assistData.isLogin) {
            openAgreeModal({
              onSubmit: async () => {
                const confirmRes = await confirmAlarm();
                if (confirmRes?.data) {
                  const alaramRes = await registerAlarm({ campaignKey: key });
                  if (alaramRes?.data) {
                    setAlarmMap(prev => ({ ...prev, [key]: true }));
                  }
                  resolve(alaramRes);
                }
              },
            });
          } else {
            openLoginConfirm();
          }
        }
      ),
    [
      assistData,
      confirmAlarm,
      openLoginConfirm,
      openAgreeModal,
      registerAlarm,
      unregisterAlarm,
    ]
  );

  // 라이브 카드 클릭 (재생 / 다시보기 / 알람)
  const handleCardClick = useCallback(
    (live: PromotionWLiveModule, index: number) => {
      const campaignKey = live.shopLiveInfo?.campaignKey;
      if (!campaignKey) return;
      const status = resolveWLiveDisplayStatus(live);

      // GA: 현재 날짜 탭의 순서(pageNo)와 타이틀(tabNm)
      const tabIndex = viewModel.dateGroups.findIndex(
        g => g.dateKey === selectedGroup?.dateKey
      );
      const pageNo = tabIndex >= 0 ? tabIndex + 1 : undefined;
      const tabScheduledStartDate =
        selectedGroup?.lives?.[0]?.shopLiveInfo?.scheduledStartDate;
      const tabNm = tabScheduledStartDate
        ? dayjs(tabScheduledStartDate).format('M.D(ddd)')
        : undefined;

      const buildPlayerUrl = () => {
        const interceptUserInfo = appInfo.isNativeAppBoth
          ? assistData.user
          : {
              userId: assistData.user?.userNo,
              gender: assistData.user?.gender,
            };
        const userInfo =
          assistData.user?.userNo === '0'
            ? ''
            : encodeURIComponent(JSON.stringify(interceptUserInfo)).replaceAll(
                '%22',
                '"'
              );
        const ak = process.env.NEXT_PUBLIC_SHOP_LIVK_KEY as string;
        const url = live.shopLiveInfo.shareUrl;
        return `https://www.shoplive.show/v1/player.html?ak=${ak}&ck=${campaignKey}&tk=${userInfo}&backButton=1&shareButton=1&shareUrl=${url}`;
      };

      if (status === 'ONAIR') {
        sendGa4ButtonClick({
          area: 'promotion_live_play_button',
          text: live.mainText,
          listNo: index + 1,
          page: 'promotion',
          pageNo,
          tabNm,
        });
        linkTo.push({ url: buildPlayerUrl() });
      } else if (status === 'ENDED') {
        sendGa4ButtonClick({
          area: 'promotion_live_replay_button',
          text: live.mainText,
          listNo: index + 1,
          page: 'promotion',
          pageNo,
          tabNm,
        });
        linkTo.push({ url: buildPlayerUrl() });
      } else {
        // READY → 알람
        const isOn = !!alarmMap[campaignKey];
        if (!isOn) {
          sendGa4ButtonClick({
            area: 'promotion_live_alarm_button',
            text: live.mainText,
            listNo: index + 1,
            pageNo,
            tabNm,
          });
        }
        onClickAlarm(campaignKey, isOn);
      }
    },
    [
      appInfo,
      assistData,
      alarmMap,
      linkTo,
      sendGa4ButtonClick,
      onClickAlarm,
      selectedGroup,
      viewModel.dateGroups,
    ]
  );

  const tabMenuList = viewModel.dateGroups.map((group, index) => {
    const repLive = group.lives[0];
    const repStatus = repLive ? resolveWLiveDisplayStatus(repLive) : 'ENDED';
    const scheduledStartDate = repLive?.shopLiveInfo?.scheduledStartDate;
    return (
      <DateTabItem
        key={group.dateKey}
        status={statusToTabValue(repStatus)}
        startDate={dayjs(scheduledStartDate).format('YYYY-MM-DD 00:00:00')}
        endDate={dayjs(scheduledStartDate).format('YYYY-MM-DD 23:59:59')}
        serverTime={getAdjustedTime()}
        value={group.dateKey}
        selectedValue={selectedGroup?.dateKey}
        type="small"
        colorType={sectionColorType}
        onClick={() => {
          sendGa4TabClick({
            area: 'promotion_live_tab',
            page: 'promotion',
            text: dayjs(scheduledStartDate).format('M.D(ddd)'),
            listNo: index + 1,
          });
          setSelectedDateKey(group.dateKey);
        }}
      />
    );
  });

  const liveItems = (selectedGroup?.lives ?? []).map((live, index) => {
    const campaignKey = live.shopLiveInfo?.campaignKey ?? '';
    const isAlarmOn = !!alarmMap[campaignKey];
    const status = resolveWLiveDisplayStatus(live);
    const { mainVideo, mainImg } = getVideo(live);
    return (
      <WLiveItem
        key={live.moduleId}
        rs={isDesktop ? '670' : '627'}
        videoSrc={mainVideo}
        videoPoster={mainImg}
        liveStatus={
          isAlarmOn && status === 'READY' ? 'notiDone' : status.toLowerCase()
        }
        liveDate={
          live.shopLiveInfo?.scheduledStartDate
            ? `${getDateAm(live.shopLiveInfo.scheduledStartDate)} ${getDateTime(
                live.shopLiveInfo.scheduledStartDate
              )}`
            : ''
        }
        liveTitle={live.mainText}
        liveDesc={live.subText}
        vid={campaignKey}
        isDesktop={isDesktop}
        onClick={() => handleCardClick(live, index)}
      />
    );
  });

  if (viewModel.dateGroups.length === 0) return null;

  return (
    <WLiveSection
      title={title}
      subTitle={subTitle}
      tabMenuList={tabMenuList}
      selectedTabId={selectedGroup?.dateKey}
      liveItem={liveItems}
      colorType={sectionColorType}
      isDesktop={isDesktop}
      initialSlide={initialSlide}
    />
  );
}

export default memo(WLiveModule);
